import {
  SupportV2ConversationMigrationConcurrency,
  SupportV2MigrationRerunKey,
  SupportV2MigrationStep,
  type SupportV2MigrationStep as SupportV2MigrationStepType,
} from "@redotech/redo-support-v2-migration/migration-execution";
import * as zExt from "@redotech/rpc/ext";
import * as z from "zod/v4";

export const MigrateTeamToSupportV2Step = SupportV2MigrationStep;
export type MigrateTeamToSupportV2Step = SupportV2MigrationStepType;
export const MigrateTeamToSupportV2RerunKey = SupportV2MigrationRerunKey;

export const MigrateTeamToSupportV2StartMode = z.discriminatedUnion("type", [
  z.object({ type: z.literal("start") }).strict(),
  z.object({ type: z.literal("resume_failed") }).strict(),
  z
    .object({
      rerunKey: MigrateTeamToSupportV2RerunKey,
      type: z.literal("terminal_rerun"),
    })
    .strict(),
]);
export type MigrateTeamToSupportV2StartMode = z.infer<
  typeof MigrateTeamToSupportV2StartMode
>;

export const migrateTeamToSupportV2 = {
  name: "migrateTeamToSupportV2" as const,
  input: z
    .object({
      teamId: zExt.objectId(),
      /**
       * Optional subset. When omitted or empty, every step runs.
       */
      steps: z.array(MigrateTeamToSupportV2Step).optional(),
      /**
       * Operator intent for the durable run lifecycle. This is intentionally a
       * discriminated union so resume and terminal rerun cannot be expressed
       * together.
       */
      startMode: MigrateTeamToSupportV2StartMode,
      /**
       * Explicit override for known fatal preflight buckets. Omit this for the
       * default behavior: fatal preflight findings block conversation writes.
       */
      allowFatalPreflight: z.boolean().optional(),
      /**
       * Bounded writer fanout for conversation materialization. Omit this to use
       * the service default.
       */
      conversationConcurrency:
        SupportV2ConversationMigrationConcurrency.optional(),
      /**
       * Run the conversation migration in incremental mode: filter the Mongo
       * cursor by `updatedAt > MAX(legacy_mongo_updated_at)` so only docs
       * changed since the last successful sync are fetched. Required for
       * cheap re-imports on teams with hundreds of thousands of
       * conversations. Requires the `team_1_updatedAt_1__id_1` index on the
       * conversations collection — the migrator's runtime safety check
       * fails the activity fast if the index isn't present.
       */
      conversationsIncrementalMode: z.boolean().optional(),
      /**
       * Run the per-conv full repair path on already-migrated conversations
       * (slow). Default is `false`: existing-ticket convs go through the
       * fast bulk-repair top-level UPDATE. Flip to `true` only when child
       * rows need reconciliation (rare).
       */
      repairExistingConversations: z.boolean().optional(),
    })
    .strict(),
  output: z.object({ workflowId: z.string() }),
};

export {
  expandSupportV2MigrationSteps,
  getSupportV2MigrationImplicitSteps,
  getSupportV2MigrationWorkflowId,
  getSupportV2MigrationWorkflowInputSteps,
  SUPPORT_V2_MIGRATION_STEP_OPTIONS as MIGRATE_TEAM_TO_SUPPORT_V2_STEP_OPTIONS,
  normalizeSupportV2MigrationPlan,
} from "@redotech/redo-support-v2-migration/migration-execution";
